import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Instagram, MapPin, Calendar } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFab from "@/components/WhatsAppFab";
import { labelFor as categoryLabelFor } from "@/lib/projectCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useInstagramEmbed(deps) {
    useEffect(() => {
        const run = () => {
            if (window.instgrm?.Embeds?.process) window.instgrm.Embeds.process();
        };
        if (document.getElementById("instagram-embed-js")) {
            run();
            return;
        }
        const s = document.createElement("script");
        s.id = "instagram-embed-js";
        s.async = true;
        s.src = "https://www.instagram.com/embed.js";
        s.onload = run;
        document.body.appendChild(s);
    }, [deps]);
}

export default function WorkDetail() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [allProjects, setAllProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setNotFound(false);
        (async () => {
            try {
                const [p, all] = await Promise.all([
                    axios.get(`${API}/projects/${slug}`),
                    axios.get(`${API}/projects`),
                ]);
                if (!alive) return;
                setProject(p.data);
                setAllProjects(all.data || []);
                document.title = `${p.data.title} — Case Study | Kaily F&B Visual Studio`;
                const md = document.head.querySelector('meta[name="description"]');
                if (md && p.data.description) md.setAttribute("content", p.data.description.slice(0, 160));
                let canon = document.head.querySelector('link[rel="canonical"]');
                if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
                canon.href = `https://slowcafe.my/work/${slug}`;
                // CreativeWork — creator Kaily Gan, publisher Kaily F&B Visual Studio, about the client brand
                let ld = document.getElementById("casestudy-jsonld");
                if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "casestudy-jsonld"; document.head.appendChild(ld); }
                ld.textContent = JSON.stringify({
                    "@context": "https://schema.org", "@type": "CreativeWork", "name": p.data.title, "url": `https://slowcafe.my/work/${slug}`,
                    "description": p.data.description || "", ...(p.data.hero_image ? { image: p.data.hero_image } : {}),
                    ...(p.data.shot_date ? { dateCreated: p.data.shot_date } : {}),
                    "creator": { "@type": "Person", "name": "Kaily Gan", "url": "https://slowcafe.my/about" },
                    "publisher": { "@type": "Organization", "name": "Kaily F&B Visual Studio", "url": "https://slowcafe.my" },
                    ...(p.data.client_name ? { about: { "@type": "Organization", "name": p.data.client_name, ...(p.data.live_url ? { url: p.data.live_url } : {}) } } : {}),
                    ...(p.data.services?.length ? { keywords: p.data.services.join(", ") } : {}),
                });
            } catch (e) {
                if (!alive) return;
                setNotFound(true);
                document.title = "Case study not found — slowcafe.my";
                let robots = document.head.querySelector('meta[name="robots"]');
                if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
                robots.setAttribute("content", "noindex, nofollow");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [slug]);

    useInstagramEmbed(project?.instagram_url);

    if (loading) {
        return (
            <Shell>
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-24 text-center text-cafe-mocha">
                    Loading…
                </div>
            </Shell>
        );
    }
    if (notFound || !project) {
        return (
            <Shell>
                <div className="max-w-3xl mx-auto px-6 md:px-12 pt-40 pb-24 text-center">
                    <div className="font-serif text-5xl text-cafe-espresso">Not found</div>
                    <p className="mt-4 text-cafe-mocha">
                        We couldn&apos;t find that case study.
                    </p>
                    <Link
                        to="/work"
                        className="mt-8 inline-flex items-center gap-2 text-cafe-olive hover:text-cafe-oliveDark"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                        Back to all work
                    </Link>
                </div>
            </Shell>
        );
    }

    const index = allProjects.findIndex((p) => p.slug === project.slug);
    const next = index >= 0 ? allProjects[(index + 1) % allProjects.length] : null;

    return (
        <Shell>
            <SeoHead project={project} />
            <article data-testid="work-detail-page">
                {/* Hero */}
                <section className="pt-28 md:pt-36">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <Link
                            to="/work"
                            className="inline-flex items-center gap-2 text-cafe-mocha hover:text-cafe-olive transition-colors text-sm mb-8"
                        >
                            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                            All work
                        </Link>
                        <div className="text-xs md:text-sm uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-4 flex flex-wrap items-center gap-3" data-testid="case-study-meta">
                            <span>{categoryLabelFor(project.category, "en") || project.category} {project.client_name ? `· ${project.client_name}` : ""}</span>
                            {project.work_type && project.work_type !== "client" && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full border border-cafe-olive/40 text-[10px] uppercase tracking-[0.18em] text-cafe-olive bg-cafe-latte/50" data-testid="case-study-worktype">
                                    {{
                                        spec: "Spec work",
                                        personal: "Personal project",
                                        collaboration: "Collaboration",
                                    }[project.work_type] || project.work_type}
                                </span>
                            )}
                            {project.in_progress && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full border border-cafe-terracotta/40 text-[10px] uppercase tracking-[0.18em] text-cafe-terracotta bg-cafe-latte/50" data-testid="case-study-inprogress">
                                    In progress
                                </span>
                            )}
                        </div>
                        <h1
                            className="font-serif text-5xl md:text-7xl lg:text-[88px] text-cafe-espresso leading-[0.92] text-balance max-w-5xl"
                            data-testid="project-title"
                            style={{ fontWeight: 500 }}
                        >
                            {project.title}
                        </h1>
                        {project.concept && (
                            <p className="mt-4 font-display italic text-xl md:text-2xl text-cafe-olive leading-snug max-w-3xl" data-testid="project-concept">
                                {project.concept}
                            </p>
                        )}
                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-cafe-mocha">
                            {project.shot_date && (
                                <span className="inline-flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-cafe-olive" strokeWidth={1.5} />
                                    {project.shot_date}
                                </span>
                            )}
                            {project.location && (
                                <span className="inline-flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-cafe-olive" strokeWidth={1.5} />
                                    {project.location}
                                </span>
                            )}
                            {project.instagram_url && (
                                <a
                                    href={project.instagram_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 hover:text-cafe-olive transition-colors"
                                >
                                    <Instagram className="h-4 w-4" strokeWidth={1.5} />
                                    View on Instagram
                                    <ArrowUpRight className="h-3.5 w-3.5" />
                                </a>
                            )}
                        </div>
                    </div>
                </section>

                {/* Hero Film — uploaded MP4 takes priority over external URL. Only published
                    case studies serve public URLs here (drafts are hidden by the public API). */}
                {project.video_file_url && !project.video_file_url.startsWith("r2key:") && (
                    <section className="mt-10 md:mt-16">
                        <div className="max-w-7xl mx-auto px-6 md:px-12">
                            <video controls playsInline preload="metadata"
                                   src={project.video_file_url}
                                   poster={project.video_poster || undefined}
                                   data-testid="work-hero-film"
                                   className="w-full rounded-2xl bg-black" />
                        </div>
                    </section>
                )}

                {/* Hero image — never cropped: landscape spans full width, portrait/square is centred and contained */}
                <section className="mt-10 md:mt-16">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <HeroImage src={project.hero_image} alt={project.title} />
                    </div>
                </section>

                {/* Description */}
                {(project.description || (project.services && project.services.length > 0)) && (
                    <section className="py-24 md:py-32">
                        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                            <div className="lg:col-span-3">
                                <div className="text-xs uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-4">
                                    The shoot
                                </div>
                                {project.services && project.services.length > 0 && (
                                    <ul className="flex flex-col gap-2 text-sm text-cafe-mocha">
                                        {project.services.map((s) => (
                                            <li key={s}>{s}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {project.description && (
                                <div className="lg:col-span-8 lg:col-start-5">
                                    <p className="font-serif text-2xl md:text-3xl text-cafe-espresso leading-snug whitespace-pre-wrap">
                                        {project.description}
                                    </p>
                                </div>
                            )}
                        </div>
                        <CaseStudyFacts project={project} />
                    </section>
                )}

                {/* Gallery — masonry, natural aspect ratios (no cropping) */}
                {project.gallery && project.gallery.length > 0 && (
                    <section className="pb-24 md:pb-32">
                        <div className="max-w-7xl mx-auto px-6 md:px-12">
                            <div className="text-xs uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-6">
                                Gallery
                            </div>
                            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6" data-testid="project-gallery">
                                {project.gallery.map((url, i) => {
                                    const cap = project.gallery_caption?.[i];
                                    const cre = project.gallery_credit?.[i];
                                    const rig = project.gallery_rights?.[i];
                                    const hasMeta = cap || cre || rig;
                                    return (
                                        <figure key={url + i} className="mb-4 md:mb-6 break-inside-avoid">
                                            <img
                                                src={url}
                                                alt={(project.gallery_alt && project.gallery_alt[i]) || `${project.title} · ${i + 1}`}
                                                loading="lazy"
                                                className="w-full h-auto rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                                            />
                                            {hasMeta && (
                                                <figcaption className="mt-2 text-[11px] leading-snug text-cafe-mocha/80 space-y-0.5" data-testid={`gallery-meta-${i}`}>
                                                    {cap && <div className="text-cafe-espresso text-xs">{cap}</div>}
                                                    {cre && <div><span className="text-cafe-mocha/60">Credit: </span>{cre}</div>}
                                                    {rig && <div><span className="text-cafe-mocha/60">Rights: </span>{rig}</div>}
                                                </figcaption>
                                            )}
                                        </figure>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}

                {/* IG embed */}
                {project.instagram_url && (
                    <section className="pb-24 md:pb-32 bg-cafe-espresso py-16 md:py-24">
                        <div className="max-w-2xl mx-auto px-6 md:px-12">
                            <div className="bg-cafe-cream rounded-3xl overflow-hidden p-2">
                                <blockquote
                                    className="instagram-media"
                                    data-instgrm-permalink={project.instagram_url}
                                    data-instgrm-version="14"
                                    style={{ background: "#FFF", border: 0, margin: 0, maxWidth: "100%", padding: 0, width: "100%" }}
                                >
                                    <div style={{ padding: 16 }}>
                                        <a href={project.instagram_url} target="_blank" rel="noreferrer" style={{ color: "#291B15" }}>
                                            View this post on Instagram
                                        </a>
                                    </div>
                                </blockquote>
                            </div>
                        </div>
                    </section>
                )}

                {/* Next project */}
                {next && next.slug !== project.slug && (
                    <section className="py-20 md:py-24">
                        <div className="max-w-7xl mx-auto px-6 md:px-12">
                            <Link
                                to={`/work/${next.slug}`}
                                className="group block bg-cafe-latte rounded-[2rem] p-8 md:p-12 hover:bg-cafe-espresso hover:text-cafe-cream transition-colors duration-500"
                                data-testid="next-project"
                            >
                                <div className="text-xs uppercase tracking-[0.25em] opacity-60 mb-3">Next case study</div>
                                <div className="flex items-center justify-between gap-6">
                                    <div className="font-serif text-3xl md:text-5xl">{next.title}</div>
                                    <ArrowUpRight className="h-8 w-8 group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </Link>
                        </div>
                    </section>
                )}
            </article>
        </Shell>
    );
}

function HeroImage({ src, alt }) {
    const [portrait, setPortrait] = useState(false);
    return (
        <div className={portrait ? "flex justify-center bg-cafe-latte rounded-[2rem] p-4 md:p-8" : ""} data-testid="hero-image-wrap">
            <img
                src={src}
                alt={alt}
                onLoad={(e) => setPortrait(e.target.naturalHeight > e.target.naturalWidth * 1.05)}
                className={portrait ? "max-h-[85vh] w-auto rounded-2xl" : "w-full h-auto rounded-[2rem]"}
            />
        </div>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso">
            <SiteNav />
            <main>{children}</main>
            <SiteFooter />
            <WhatsAppFab />
        </div>
    );
}


// Editable case-study facts — rendered only when Kaily filled them in (nothing is invented).
function CaseStudyFacts({ project: p }) {
    const rows = [
        ["Client", p.client_name], ["Business category", p.business_category], ["Project location", p.location], ["Project date", p.shot_date],
        ["Services provided", p.services?.length ? p.services.join(" · ") : ""], ["Deliverables", p.deliverables?.length ? p.deliverables.join(" · ") : ""],
    ].filter(([, v]) => v);
    // Phase 6 — 9-section director case-study scaffold. Each block only renders
    // when the editor filled it in. Nothing is auto-generated or invented.
    const blocks = [
        ["Concept", p.concept],
        ["Brand objective", p.objective],
        ["Creative direction", p.creative_direction],
        ["Client challenge", p.challenge],
        ["My role & key decisions", p.kaily_role],
        ["Deliverables", p.deliverables?.length ? p.deliverables.join("\n") : ""],
        ["Distribution & verified results", p.distribution],
        ["Reported results", p.results],
    ].filter(([, v]) => v);
    if (!rows.length && !blocks.length && !p.testimonial && !p.live_url) return null;
    return (
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="case-study-facts">
            <dl className="lg:col-span-4 space-y-4">
                {rows.map(([k, v]) => <div key={k}><dt className="text-[10px] tracking-[0.24em] uppercase text-cafe-olive font-semibold">{k}</dt><dd className="text-sm text-cafe-espresso mt-1">{v}</dd></div>)}
            </dl>
            <div className="lg:col-span-8 space-y-8">
                {blocks.map(([k, v]) => <div key={k} data-testid={`case-study-block-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><h3 className="text-[10px] tracking-[0.24em] uppercase text-cafe-olive font-semibold mb-2">{k}</h3><p className="text-base text-cafe-mocha leading-relaxed whitespace-pre-wrap">{v}</p></div>)}
                {p.testimonial && <blockquote className="border-l-2 border-[#D4A574] pl-5 font-serif text-xl text-cafe-espresso leading-snug" data-testid="case-study-testimonial">“{p.testimonial}”{p.testimonial_by && <footer className="text-sm text-cafe-mocha mt-2 not-italic">— {p.testimonial_by}</footer>}</blockquote>}
                <div className="flex flex-wrap gap-3 pt-2">
                    {p.live_url && <a href={p.live_url} target="_blank" rel="noopener noreferrer" data-testid="case-study-live-link" className="inline-flex items-center px-5 py-2.5 rounded-full bg-cafe-espresso text-cafe-cream text-sm font-semibold">Visit the live {p.client_name || "merchant"} page →</a>}
                    <Link to={`/?source=case:${p.slug}#contact`} data-testid="case-study-enquiry" className="inline-flex items-center px-5 py-2.5 rounded-full bg-cafe-olive text-cafe-cream text-sm font-semibold">Discuss a similar project</Link>
                    <a href={`https://wa.me/60183628663?text=${encodeURIComponent(`Hi Kaily! I saw the ${p.client_name || ""} case study and would like to discuss a similar project.`)}`} target="_blank" rel="noopener noreferrer" data-testid="case-study-whatsapp" className="inline-flex items-center px-5 py-2.5 rounded-full border border-cafe-espresso/20 text-cafe-espresso text-sm font-semibold">WhatsApp Kaily</a>
                </div>
            </div>
        </div>
    );
}

// ---------- SEO head (2026-02 case-study editor overhaul) ----------
// Writes real <title>, <meta description>, <link canonical> and OpenGraph /
// Twitter tags into document.head for each case study. Uses direct DOM
// manipulation (not react-helmet) to override the baseline tags shipped in
// public/index.html — matches the pattern already used by PublicLanding.jsx
// and lib/seo.js elsewhere in this codebase. Content comes from the project
// doc only — never from an LLM or external source.
function SeoHead({ project }) {
    useEffect(() => {
        if (!project) return;
        const siteBase = (typeof window !== "undefined" && window.location.origin) || "https://slowcafe.my";
        const canonicalBase = "https://slowcafe.my"; // SEO canonical always points at production domain
        const canonical = `${canonicalBase}/work/${project.slug}`;
        const clientPrefix = project.client_name ? `${project.client_name} · ` : "";
        const title = (project.seo_title && project.seo_title.trim())
            || `${clientPrefix}${project.category || "Case Study"} — Kaily F&B Visual Studio`;
        const desc = (project.meta_description && project.meta_description.trim())
            || (project.description || "").replace(/\s+/g, " ").trim().slice(0, 240)
            || "A case study by Kaily F&B Visual Studio — Brand Film, Campaign and Content Production for F&B brands.";
        const image = (project.og_image && project.og_image.trim())
            || (project.video_poster && !String(project.video_poster).startsWith("r2key:") ? project.video_poster : null)
            || (project.hero_image && !String(project.hero_image).includes("placeholder") ? project.hero_image : null)
            || `${siteBase}/og-default.jpg`;

        const ensure = (selector, make) => {
            let el = document.head.querySelector(selector);
            if (!el) { el = make(); document.head.appendChild(el); }
            return el;
        };
        const setMeta = (prop, attr, name, content) => {
            const el = ensure(`meta[${prop}="${name}"]`, () => {
                const m = document.createElement("meta");
                m.setAttribute(prop, name);
                return m;
            });
            el.setAttribute("content", content);
            el.setAttribute("data-case-study-seo", "1");
        };

        const prevTitle = document.title;
        document.title = title;
        setMeta("name", "description", "description", desc);
        setMeta("name", "robots", "robots", "index, follow");
        setMeta("property", "og:type", "og:type", "article");
        setMeta("property", "og:url", "og:url", canonical);
        setMeta("property", "og:title", "og:title", title);
        setMeta("property", "og:description", "og:description", desc);
        setMeta("property", "og:image", "og:image", image);
        setMeta("name", "twitter:card", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", "twitter:title", title);
        setMeta("name", "twitter:description", "twitter:description", desc);
        setMeta("name", "twitter:image", "twitter:image", image);

        const canon = ensure('link[rel="canonical"]', () => {
            const l = document.createElement("link");
            l.rel = "canonical";
            return l;
        });
        canon.setAttribute("href", canonical);

        // JSON-LD structured data (CreativeWork + optional VideoObject). Only
        // fields we actually have are emitted — no fabricated results or
        // audience claims. Public URLs only (no r2key: markers).
        const graph = [{
            "@type": "CreativeWork",
            "@id": canonical,
            "name": title,
            "description": desc,
            "url": canonical,
            "author": {
                "@type": "Person",
                "name": "Kaily Gan",
                "jobTitle": "F&B and Hospitality Filmmaker",
                "worksFor": { "@type": "Organization", "name": "Kaily F&B Visual Studio" }
            },
            "image": image,
            "inLanguage": "en",
            ...(project.client_name ? { "about": { "@type": "Organization", "name": project.client_name } } : {}),
            ...(project.location ? { "contentLocation": { "@type": "Place", "name": project.location } } : {}),
            ...(project.shot_date ? { "dateCreated": project.shot_date } : {}),
        }];
        const vidUrl = project.video_file_url && !String(project.video_file_url).startsWith("r2key:") ? project.video_file_url : null;
        const posterUrl = project.video_poster && !String(project.video_poster).startsWith("r2key:") ? project.video_poster : null;
        if (vidUrl) {
            graph.push({
                "@type": "VideoObject",
                "name": title,
                "description": desc,
                "thumbnailUrl": posterUrl || image,
                "contentUrl": vidUrl,
                "uploadDate": project.shot_date || new Date().toISOString().slice(0, 10),
                "inLanguage": "en",
            });
        }
        let ld = document.getElementById("case-study-jsonld");
        if (!ld) {
            ld = document.createElement("script");
            ld.type = "application/ld+json";
            ld.id = "case-study-jsonld";
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });

        return () => {
            // On unmount, restore page title; leave meta content in place
            // (the next route's own SEO head will overwrite as needed).
            document.title = prevTitle;
            const ld = document.getElementById("case-study-jsonld");
            if (ld) ld.remove();
        };
    }, [project]);
    return null;
}
